import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'
import { TreeRings } from '@/components/generals/tree-rings'
import { buttonVariants } from '@/components/ui/button'
import { PROCESS_STEPS } from '@/constants/content'

export const ProcessSteps: FC = (): ReactNode => {
    return (
        <section id='process' className='relative overflow-hidden bg-forest py-20 text-forest-foreground lg:py-28'>
            <TreeRings className='pointer-events-none absolute -bottom-64 -left-48 size-[40rem] text-forest-foreground/[0.06]' />

            <div className='relative mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        tone='light'
                        eyebrow="How it works"
                        title="From first call to your front door"
                        description="Building a home should feel exciting, not stressful. Our process is simple, transparent and built around a fixed price and a fixed schedule."
                    />

                    <Link
                        href='/process'
                        className={cn(buttonVariants({ variant: 'outline-light', size: 'lg' }), 'self-start lg:self-auto')}
                    >
                        See how we build
                        <ArrowRight className='size-4' />
                    </Link>
                </div>

                <ol className='mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4'>
                    {PROCESS_STEPS.map(step => (
                        <li
                            key={step.number}
                            className='flex flex-col rounded-2xl border border-forest-foreground/12 bg-forest-foreground/[0.04] p-7 transition-colors duration-300 hover:bg-forest-foreground/[0.08]'
                        >
                            <div className='flex items-center justify-between'>
                                <span className='flex size-12 items-center justify-center rounded-xl bg-forest-foreground/10'>
                                    <step.icon className='size-6' aria-hidden='true' />
                                </span>

                                <span className='font-heading text-4xl font-medium text-forest-foreground/25'>
                                    {step.number}
                                </span>
                            </div>

                            <h3 className='mt-8 font-heading text-2xl font-medium'>
                                {step.title}
                            </h3>

                            <p className='mt-3 flex-1 leading-relaxed text-forest-foreground/70'>
                                {step.text}
                            </p>

                            <p className='mt-6 inline-flex self-start rounded-full bg-timber px-3 py-1 text-xs font-semibold text-timber-foreground'>
                                {step.meta}
                            </p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}
